<!--
  - Copyright (c) 2023. Haodong JU
  -->

<template>
  <div>
    <ik-data-table
      ref="dataTable"
      :custom-on-row-click="goto"
      :model="model"
      :use-action="false"
      :use-default-action="false"
      entity-name="ItalyXML"
      icon="mdi-cards"
    >
      <template
        #item.sendEmail="{item}"
      >
        <v-btn
          :disabled="item.pecSend !== '0'"
          :height="!$vuetify.breakpoint.lgAndUp ? '40px' : ''"
          :icon="!$vuetify.breakpoint.lgAndUp"
          :style="!$vuetify.breakpoint.lgAndUp ? 'border-radius: 12px; background: #f57c00;' : ''"
          :width="!$vuetify.breakpoint.lgAndUp ? '40px' : ''"
          color="amber lighten-4 black--text"
          elevation="0"
          small
          @click.stop="sendEmail(item)"
        >
          <template v-if="!$vuetify.breakpoint.lgAndUp">
            <v-icon color="white">
              mdi-cog
            </v-icon>
          </template>
          <template v-if="$vuetify.breakpoint.lgAndUp">
            <div
              :class="!$vuetify.breakpoint.lgAndUp ? 'text-truncate' : ''"
              :style="!$vuetify.breakpoint.lgAndUp ? 'max-width: 100px;' : ''"
            >
              {{ $t('send') }}
            </div>
          </template>
        </v-btn>
      </template>
    </ik-data-table>
    <v-dialog
      v-model="updateXMLDialog"
      max-width="800px"
      persistent
    >
      <base-form
        :can-edit="editObj.pecSend === '0'"
        :editing-object="editObj"
        :schema="ItalySchemas"
        @close="updateXMLDialog=false"
        @submit="updateXML"
      >
        <div>{{ $t('PrintMenus') }}</div>
        <template #subtitle>
          {{ $t('PrintMenusSubtitle') }}
        </template>
      </base-form>
    </v-dialog>
  </div>
</template>

<script>
import IkDataTable from '@/plugins/EntityFrameWork/IkDataTable.vue'
import ItalyXML from '@/model/dataLayer/api/Italy/ItalyXML'
import BaseForm from '@/model/baseForm/BaseForm/BaseForm.vue'
import DateDayPicker from '@/views/widget/commonLogic/DateDayPicker.vue'
import i18n from '@/i18n'
import { sendFPAEmail, updateFPA } from '@/model/dataLayer/api/Italy/ItalyBills'
import { showSuccessMessage } from '@/model/utils/utils'

export default {
  name: 'ItalyXML',
  components: {
    BaseForm,
    IkDataTable
  },
  data () {
    return {
      orderId: '',
      updateXMLDialog: false,
      editObj: {},
      model: ItalyXML
    }
  },
  computed: {
    ItalySchemas () {
      return [
        {
          key: 'date',
          name: 'Data documento',
          component: DateDayPicker,
          componentProps: {
            locale: i18n.locale
          }
        },
        {
          key: 'pivaPaese',
          name: 'P.IVA Paese',
          componentProps: {
            rules: [v => /^[A-Z]{2}$/.test(v) || '[A-Z]{2}']
          }
        },
        {
          key: 'piva',
          name: 'P.IVA',
          componentProps: {
            rules: [v => /^.{1,28}$/.test(v) || '1-28']
          }
        },
        {
          key: 'codiceFiscale',
          name: 'Codice Fiscale',
          required: false,
          default: '',
          componentProps: {
            rules: [v => {
              if (v !== '') {
                return /^[A-Z0-9]{11,16}$/.test(v) || '[A-Z] [0-9] 11-16'
              }
            }]
          }
        },
        {
          key: 'codiceDestinatario',
          name: 'Codice Amministrazione destinataria',
          default: '0000000',
          componentProps: {
            rules: [v => /^[A-Z0-9]{6,7}$/.test(v) || '[A-Z0-9]{6,7}']
          }
        },
        {
          key: 'pecDestinatario',
          name: 'Destinatario PEC',
          required: false,
          default: '',
          componentProps: {
            rules: [v => {
              if (!v) {
                v = ''
              }
              if (v !== '') {
                return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(v) || i18n.t('EnterEmailAddress')
              }
            }]
          }
        },
        {
          key: 'denominazione',
          name: 'Denominazione',
          componentProps: {
            rules: [v => /^[\x20-\xFF]{1,80}$/.test(v) || '1-80']
          }
        },
        {
          key: 'indirizzo',
          name: 'Indirizzo',
          componentProps: {
            rules: [v => /^[\x20-\xFF]{1,60}$/.test(v) || '1-60']
          }
        },
        {
          key: 'cap',
          name: 'CAP',
          componentProps: {
            rules: [v => /^[0-9]{5}$/.test(v) || '[0-9] 5']
          }
        },
        {
          key: 'comune',
          name: 'Comune',
          componentProps: {
            rules: [v => /^[\x20-\xFF]{1,60}$/.test(v) || '1-60']
          }
        },
        {
          key: 'provincia',
          name: 'Provincia',
          componentProps: {
            rules: [v => /^[A-Z]{2}$/.test(v) || '[A-Z]{2}']
          }
        },
        {
          key: 'nazione',
          name: 'Nazione',
          componentProps: {
            rules: [v => /^[A-Z]{2}$/.test(v) || '[A-Z]{2}']
          }
        },
        {
          key: 'causale',
          name: 'Causale',
          required: false,
          default: '',
          componentProps: {
            rules: [v => /^[\x20-\xFF]*$/.test(v) || '']
          }
        }
      ]
    }
  },
  mounted () {
    this.orderId = this.$route.query.orderId
    if (this.orderId) {
      this.$refs.dataTable.search = this.orderId
    }
  },
  methods: {
    async sendEmail (item) {
      await sendFPAEmail(item.id)
      showSuccessMessage(i18n.t('submit_success'))
      await this.$refs.dataTable.reload()
    },
    async updateXML (info) {
      await updateFPA(info)
      this.updateXMLDialog = false
      await this.$refs.dataTable.reload()
    },
    goto (item) {
      this.editObj = item
      this.updateXMLDialog = true
    }
  }
}
</script>

<style scoped>

</style>
